/**
 * GuardianCenterPage — the Guardian Center (admin/owner, read-only), the single consuming
 * surface of the composed GET /api/agents/guardian-center contract: the guardians' domains
 * (scan, findings, backup, connections), the durable audit trail and the escalation tickets,
 * each with severity, freshness, provenance, escalation state and related employees/work.
 *
 * Read-only by contract — the only state change possible on this page is a refetch. Running a
 * scan, triaging a finding, opening a ticket or changing backup/connection settings all stay
 * on their existing pages, which this page links to. States are rendered exactly as the
 * contract reports them: stale or unknown evidence never reads as current health.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { guardianCenterApi } from "../services/api";
import { ticketStatusLabel } from "../components/ticketStatusVocabulary";
import { useAuth } from "../context/AuthContext";
import EmployeeAvatar from "../components/EmployeeAvatar";
import { runStatusLabel } from "../components/runStatusVocabulary";
import { parseUtc } from "../utils/utcDate";

// ─── Vocabulary ──────────────────────────────────────────────────────────────

const STATE_TONE = {
  healthy:   "bg-emerald-50 text-emerald-800 border-emerald-200",
  attention: "bg-amber-50 text-amber-800 border-amber-200",
  degraded:  "bg-orange-50 text-orange-800 border-orange-300",
  critical:  "bg-red-50 text-red-800 border-red-300",
  stale:     "bg-slate-100 text-slate-700 border-slate-300",
  unknown:   "bg-gray-100 text-gray-700 border-gray-300",
};
const STATE_LABEL_FALLBACK = {
  healthy:   "תקין",
  attention: "לתשומת לב",
  degraded:  "לא תקין",
  critical:  "קריטי",
  stale:     "מידע מיושן",
  unknown:   "לא ידוע",
};

const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
const SEVERITY_BADGE = {
  critical: "bg-red-100 text-red-700 border-red-300",
  high:     "bg-orange-100 text-orange-700 border-orange-300",
  medium:   "bg-amber-100 text-amber-700 border-amber-300",
  low:      "bg-blue-100 text-blue-700 border-blue-300",
};
const SEVERITY_LABEL = { critical: "קריטי", high: "גבוה", medium: "בינוני", low: "נמוך" };

// The finding triage ladder as the backend contract reports it (escalation_state).
const ESCALATION_STATE_LABEL = {
  awaiting_triage: "ממתין לטיפול",
  in_treatment:    "בטיפול",
  watched:         "במעקב",
  handled:         "טופל",
  hidden:          "הוסתר",
};

const CATEGORY_LABEL = {
  ticket_health:  "בריאות קריאות שירות",
  ai_health:      "בריאות מנוע AI",
  user_access:    "חשבונות והרשאות",
  scanner_health: "בריאות הסורק",
};

const TICKET_PRIORITY_LABEL = { high: "גבוהה", medium: "בינונית", low: "נמוכה" };

const ESCALATION_ORIGIN_LABEL = { finding: "ממצא", backup: "גיבוי" };

const RELATION_LABEL = {
  scan_owner:              "בעל הסריקה",
  scan_run:                "ריצת הסריקה",
  connection_owner:        "בעל החיבור",
  connection_untested:     "חיבור לא נבדק",
  connection_failed:       "חיבור שנכשל",
  failed_run:              "ריצה שנכשלה",
  stuck_run:               "ריצה תקועה",
  awaiting_review_run:     "ריצה הממתינה לסקירה",
  ticket_work:             "עבודה על קריאה",
  ticket_work_run:         "ריצה על הקריאה",
  stale_high_priority_ticket: "קריאה בעדיפות גבוהה שמוזנחת",
  escalation_work:         "עבודה על הסלמה",
  escalation_ticket:       "קריאת הסלמה",
};

const SYSTEM_TYPE_LABEL = {
  ESET_PROTECT:   "ESET",
  M365_GRAPH:     "Microsoft 365",
  ACRONIS_BACKUP: "Acronis",
};

// Connection-test evidence grading, mirroring the vocabulary of the specialist pages: ok/success
// are the only verified tokens, an explicit failure word reads as failed, every other non-empty
// token stays unknown, and a verified token is only "verified" while its timestamp is fresh.
const CONNECTION_EVIDENCE_MAX_AGE_HOURS = 25;
const CONNECTION_FUTURE_SKEW_MS = 5 * 60 * 1000;
const CONNECTION_TONE_CLS = {
  green: "text-emerald-700",
  amber: "text-amber-700",
  red: "text-red-600",
  gray: "text-gray-500",
};
const FAILURE_TOKEN_WORDS = new Set([
  "fail", "failed", "failure", "error", "denied", "invalid", "unauthorized", "rejected",
]);

function isFailureStatusToken(raw) {
  return String(raw)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .some((word) => FAILURE_TOKEN_WORDS.has(word));
}

export function connectionRowEvidence(row, now = Date.now()) {
  const raw = typeof row?.last_test_status === "string" ? row.last_test_status.trim() : "";
  if (!raw) return { state: "never", label: "טרם נבדק", tone: "gray", rawStatus: null, ageHours: null };
  const token = raw.toLowerCase();
  const verified = token === "ok" || token === "success";
  const testedAt = row?.last_tested_at ? parseUtc(row.last_tested_at) : null;
  const testedMs = testedAt ? testedAt.getTime() : NaN;
  const ageHours = Number.isFinite(testedMs) ? (now - testedMs) / 3_600_000 : null;
  if (!verified && isFailureStatusToken(raw)) {
    return { state: "failed", label: "הבדיקה נכשלה", tone: "red", rawStatus: raw, ageHours };
  }
  if (!verified) {
    return { state: "unknown", label: "מצב בדיקה לא מזוהה", tone: "amber", rawStatus: raw, ageHours };
  }
  if (ageHours == null) {
    return { state: "ok-no-timestamp", label: "אימות ללא חתימת זמן", tone: "amber", rawStatus: null, ageHours: null };
  }
  if (ageHours < 0 && -ageHours * 3_600_000 > CONNECTION_FUTURE_SKEW_MS) {
    return { state: "unreliable", label: "הראיה אינה אמינה", tone: "red", rawStatus: null, ageHours };
  }
  if (ageHours > CONNECTION_EVIDENCE_MAX_AGE_HOURS) {
    return { state: "stale", label: "הראיה ישנה", tone: "amber", rawStatus: null, ageHours };
  }
  return { state: "ok", label: "אימות תקין", tone: "green", rawStatus: null, ageHours };
}

const DOMAIN_ORDER = ["platform_scan", "security_findings", "backup", "connections"];
const DOMAIN_TITLE = {
  platform_scan:     "שומר פלטפורמה — סריקה",
  security_findings: "ממצאים",
  backup:            "שומר גיבויים",
  connections:       "חיבורים",
};
const DOMAIN_ICON = {
  platform_scan:     "🔒",
  security_findings: "🛡️",
  backup:            "💾",
  connections:       "🔌",
};

const SEVERITY_FILTERS = [
  { key: "all",      label: "כל החומרות" },
  { key: "critical", label: "קריטי" },
  { key: "high",     label: "גבוה" },
  { key: "medium",   label: "בינוני" },
  { key: "low",      label: "נמוך" },
];

const EVIDENCE_MAX_VALUE_CHARS = 140;
// The backend already projects evidence to bounded scalars; this is defense in depth on top
// of it, matching the findings pages' own convention.
const EVIDENCE_SECRET_KEY_RE = /(secret|token|password|passwd|api[_-]?key|credential|private)/i;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  const d = parseUtc(iso);
  if (!d || Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(d);
}

function fmtAge(hours) {
  if (hours == null) return "לא ידוע";
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} דקות`;
  if (hours < 48) return `${Math.round(hours)} שעות`;
  return `${Math.round(hours / 24)} ימים`;
}

function hoursSince(iso) {
  if (!iso) return null;
  const parsed = parseUtc(iso);
  const t = parsed ? parsed.getTime() : NaN;
  if (!Number.isFinite(t)) return null;
  return Math.max(0, (Date.now() - t) / 3_600_000);
}

function isScalar(v) {
  return v == null || ["string", "number", "boolean"].includes(typeof v);
}

function truncate(v) {
  const s = String(v);
  return s.length > EVIDENCE_MAX_VALUE_CHARS ? `${s.slice(0, EVIDENCE_MAX_VALUE_CHARS)}…` : s;
}

function severityRank(sev) {
  return SEVERITY_RANK[sev] ?? 4;
}

function severityLabel(sev) {
  return SEVERITY_LABEL[sev] ?? (sev || "לא ידוע");
}

function stateTone(state) {
  return STATE_TONE[state] || STATE_TONE.unknown;
}

function stateLabel(state, label) {
  return label || STATE_LABEL_FALLBACK[state] || state;
}

// ─── Small shared pieces ─────────────────────────────────────────────────────

function StateChip({ state, label, testId }) {
  return (
    <span
      data-testid={testId}
      className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${stateTone(state)}`}
    >
      {stateLabel(state, label)}
    </span>
  );
}

function SectionCard({ title, icon, badge, children, testId }) {
  return (
    <section data-testid={testId} className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-semibold text-gray-700">
          {icon && <span aria-hidden="true">{icon}</span>} {title}
        </h2>
        {badge}
      </div>
      {children}
    </section>
  );
}

function SeverityBadge({ severity }) {
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${SEVERITY_BADGE[severity] || "bg-gray-100 text-gray-600 border-gray-300"}`}>
      {severityLabel(severity)}
    </span>
  );
}

// ─── Related employees / work (the employee & work dimension) ────────────────

function RelatedEmployeeRow({ employee }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <EmployeeAvatar name={employee.name} status={employee.availability_status} isEnabled={employee.is_enabled} size="sm" />
      <Link to={`/agents/${employee.agent_id}`} className="text-brand-600 hover:underline font-medium">
        {employee.name}
      </Link>
      {employee.role_title && <span className="text-gray-500">{employee.role_title}</span>}
      <span className="text-gray-400">{RELATION_LABEL[employee.relation] ?? employee.relation}</span>
    </div>
  );
}

function WorkRow({ item }) {
  if (!item) return null;
  if (item.kind === "ticket") {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span aria-hidden="true">📋</span>
        <Link to={`/tickets/${item.ticket_id ?? item.id}`} className="text-brand-600 hover:underline">
          {item.ticket_number != null ? `#${item.ticket_number} ` : ""}{item.title ?? "קריאה"}
        </Link>
        <span className="text-gray-500">{item.status ? ticketStatusLabel(item.status) : "—"}</span>
        <span className="text-gray-400">{RELATION_LABEL[item.relation] ?? item.relation}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span aria-hidden="true">⚡</span>
      <Link to={`/agent-runs/${item.id}`} className="text-brand-600 hover:underline">
        ריצה{item.agent_name ? ` של ${item.agent_name}` : ""}
      </Link>
      <span className="text-gray-500">{item.status ? runStatusLabel(item.status) : "—"}</span>
      {item.at && <span className="text-gray-400">לפני {fmtAge(hoursSince(item.at))}</span>}
      {item.ticket_id && (
        <Link to={`/tickets/${item.ticket_id}`} className="text-brand-600 hover:underline">
          לקריאה ←
        </Link>
      )}
      <span className="text-gray-400">{RELATION_LABEL[item.relation] ?? item.relation}</span>
    </div>
  );
}

function RelatedBlock({ related }) {
  if (!related) return null;
  const employees = Array.isArray(related.employees) ? related.employees : [];
  const work = Array.isArray(related.work) ? related.work : [];
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50 px-2 py-1.5 space-y-1 text-xs">
      <div className="text-gray-500">
        בסיס הקישור: <span className="text-gray-600">{related.basis}</span>
      </div>
      {employees.length === 0 && work.length === 0 ? (
        <div className="text-gray-500 italic">לא נמצא קישור עובד/עבודה רשום</div>
      ) : (
        <>
          {employees.map((e, i) => <RelatedEmployeeRow key={`${e.agent_id}-${i}`} employee={e} />)}
          {work.map((w) => <WorkRow key={`${w.kind}-${w.id}`} item={w} />)}
        </>
      )}
    </div>
  );
}

// ─── Evidence ────────────────────────────────────────────────────────────────

function EvidenceDisclosure({ entries }) {
  const rendered = useMemo(() => {
    const out = [];
    for (const entry of Array.isArray(entries) ? entries : []) {
      if (!entry || typeof entry !== "object") continue;
      const rows = Object.entries(entry).filter(
        ([key, value]) => !EVIDENCE_SECRET_KEY_RE.test(key) && isScalar(value)
      );
      out.push(rows);
    }
    return out;
  }, [entries]);

  if (rendered.length === 0) return null;

  return (
    <details className="text-xs text-gray-500 border-t border-gray-100 pt-1">
      <summary className="cursor-pointer hover:text-gray-700 select-none">
        ראיות מצורפות ({rendered.length})
      </summary>
      <div className="mt-1 space-y-1.5">
        {rendered.map((rows, i) => (
          <div key={i} className="border border-gray-100 rounded px-2 py-1 bg-gray-50 space-y-0.5">
            {rows.length === 0 ? (
              <div className="text-gray-400">אין שדות ראיה להצגה</div>
            ) : (
              rows.map(([key, value]) => (
                <div key={key} className="flex gap-2">
                  <span className="text-gray-400 shrink-0" dir="ltr">{key}:</span>
                  <span className="text-gray-600 break-all" dir="ltr">{truncate(value)}</span>
                </div>
              ))
            )}
          </div>
        ))}
      </div>
    </details>
  );
}

// ─── Overall banner ──────────────────────────────────────────────────────────

function overallNote(state) {
  switch (state) {
    case "healthy":
      return "כל תחומי השומרים מדווחים תקין";
    case "critical":
    case "degraded":
    case "attention":
      return "קיים מצב פעיל — הפירוט לפי תחום בהמשך העמוד";
    case "stale":
      return "לפחות תחום אחד מבוסס ראיות מיושנות — אין להסיק מצב נוכחי";
    case "unknown":
      return "לפחות תחום אחד ללא ראיות — אין להסיק מצב תקין";
    default:
      return null;
  }
}

function OverallBanner({ failed, loading, stale, data, onRetry }) {
  if (failed && !data) {
    // No snapshot was ever loaded: the state is unknown, not merely old — say so and offer the
    // one safe action (a read-only refetch).
    return (
      <div role="alert" data-testid="guardian-center-overall" className="rounded-xl border px-4 py-3 text-sm font-medium bg-gray-50 border-gray-300 text-gray-700 space-y-2">
        <div>לא ניתן לטעון את מרכז השומרים — המצב אינו ידוע</div>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs border rounded-lg px-3 py-1.5 hover:bg-gray-100 font-normal"
        >
          נסה שוב
        </button>
      </div>
    );
  }
  if (loading || !data) {
    return (
      <div role="status" data-testid="guardian-center-overall" className="rounded-xl border px-4 py-3 text-sm font-medium bg-gray-50 border-gray-200 text-gray-500">
        טוען את מרכז השומרים…
      </div>
    );
  }
  const state = data.overall_state;
  const note = overallNote(state);
  return (
    <div className="space-y-2">
      {stale && (
        // A failed refresh keeps the last successful snapshot on screen; it is marked as exactly
        // that, so a stale "healthy" can never read as the current state.
        <div
          role="status"
          data-testid="guardian-center-stale"
          className="rounded-xl border px-4 py-3 text-sm bg-slate-100 border-slate-300 text-slate-700 flex items-center justify-between gap-2 flex-wrap"
        >
          <span>הרענון האחרון נכשל — הנתונים המוצגים הם מהטעינה האחרונה שהצליחה.</span>
          <button type="button" onClick={onRetry} className="text-brand-600 hover:underline shrink-0">
            נסה שוב
          </button>
        </div>
      )}
      <div
        role="status"
        data-testid="guardian-center-overall"
        className={`rounded-xl border px-4 py-3 text-sm ${stateTone(state)}`}
      >
        <div className="font-bold">מצב כולל: {stateLabel(state, data.overall_state_label)}</div>
        {note && <div className="mt-0.5">{note}</div>}
      </div>
    </div>
  );
}

// ─── Domain cards ────────────────────────────────────────────────────────────

function ScanDetails({ details }) {
  if (!details) return null;
  return (
    <div className="text-xs text-gray-500 space-y-0.5">
      <div>
        סוכן:{" "}
        {details.agent_enabled === true ? (
          <span className="text-emerald-700">פעיל</span>
        ) : details.agent_enabled === false ? (
          <span className="text-red-600">מושבת</span>
        ) : (
          "לא ידוע"
        )}
      </div>
      {details.never_ran ? (
        <div>טרם בוצעה סריקה של השומר</div>
      ) : (
        <>
          {details.last_run_status && (
            <div>סטטוס ריצה אחרונה: {runStatusLabel(details.last_run_status)}</div>
          )}
          {details.last_run_at && <div>בוצע: {fmtDate(details.last_run_at)}</div>}
        </>
      )}
    </div>
  );
}

function FindingsDetails({ details }) {
  if (!details) return null;
  return (
    <div className="text-xs text-gray-500 space-y-0.5">
      <div>
        פתוחים: <strong>{details.open_total ?? 0}</strong>
        {" · "}קריטיים פתוחים: <strong>{details.open_critical ?? 0}</strong>
        {" · "}גבוהים פתוחים: <strong>{details.open_high ?? 0}</strong>
      </div>
      <div>סה"כ ברשומת הממצאים: {details.total ?? 0}</div>
      {details.scan_never_ran && <div>טרם בוצעה סריקת אבטחה — הרשומה ריקה מראיות</div>}
      <div className="text-gray-400">פירוט הממצאים בהמשך העמוד</div>
    </div>
  );
}

function BackupDetails({ details }) {
  if (!details) return null;
  if (!details.scan_performed) {
    return (
      <div className="text-xs text-gray-500 space-y-0.5">
        <div>לא בוצעה סריקת תיקיית גיבויים — אין ראיות להצגה</div>
        {details.directory && <div dir="ltr" className="text-gray-400">נתיב: {details.directory}</div>}
      </div>
    );
  }
  return (
    <div className="text-xs text-gray-500 space-y-0.5">
      <div>חומרה שדווחה: {details.severity ?? "—"}</div>
      {details.last_backup && <div>גיבוי אחרון: {fmtDate(details.last_backup)}</div>}
      {details.backup_count != null && <div>מספר גיבויים: {details.backup_count}</div>}
      {details.age_hours != null && (
        <div>
          גיל הגיבוי האחרון: {fmtAge(details.age_hours)}
          {details.max_age_hours != null && ` (מקסימום מותר: ${details.max_age_hours} שעות)`}
        </div>
      )}
      {details.min_count != null && <div>מינימום גיבויים נדרש: {details.min_count}</div>}
    </div>
  );
}

function connectionExtra(integration) {
  const parts = [];
  if (integration.mode_label) parts.push(integration.mode_label);
  if (integration.key === "teamviewer" && integration.credential_source === "invalid_reference") {
    parts.push("הפניית סוד אינה תקינה");
  }
  if (integration.provider || integration.model) {
    parts.push([integration.provider, integration.model].filter(Boolean).join(" / "));
  }
  return parts;
}

function ConnectionsDetails({ details }) {
  if (!details) return null;
  const integrations = Array.isArray(details.integrations) ? details.integrations : [];
  const totals = details.employee_connections_totals || {};
  const rows = Array.isArray(details.employee_connections) ? details.employee_connections : [];
  const waiting = details.tools_waiting_for_node ?? 0;
  return (
    <div className="text-xs text-gray-500 space-y-1">
      <div className="space-y-0.5">
        {integrations.map((integration) => (
          <div key={integration.key} className="flex items-center gap-1.5 flex-wrap">
            <span
              aria-hidden="true"
              className={`inline-block w-1.5 h-1.5 rounded-full ${integration.configured ? "bg-emerald-500" : "bg-gray-400"}`}
            />
            <span>
              {integration.label}: {integration.configured ? "מוגדר" : "לא מוגדר"}
              {connectionExtra(integration).map((extra) => ` (${extra})`).join("")}
            </span>
          </div>
        ))}
      </div>
      {waiting > 0 && (
        <div className="text-amber-700">{waiting} כלים ממתינים לצומת OpenClaw מחובר</div>
      )}
      {totals.connections != null && (
        <div>
          חיבורי עובדים: {totals.connections} · בדיקה אחרונה תקינה: {totals.tested_ok ?? 0} · בדיקה אחרונה נכשלה: {totals.failed ?? 0} · לא נבדקו: {totals.never_tested ?? 0}
        </div>
      )}
      {details.employee_connections_latest_tested_at && (
        <div>נבדק לאחרונה: {fmtDate(details.employee_connections_latest_tested_at)}</div>
      )}
      <div className="text-gray-400">מבוסס תצורה ורשומות בדיקה קיימות — ללא בדיקה חיה</div>
      {rows.length > 0 && (
        <details className="pt-0.5">
          <summary className="cursor-pointer hover:text-gray-700 select-none">
            חיבורי עובדים ({rows.length})
          </summary>
          <div className="mt-1 space-y-1">
            {rows.map((row) => {
              const evidence = connectionRowEvidence(row);
              return (
                <div key={row.connection_id} className="flex items-center gap-2 flex-wrap border border-gray-100 rounded px-2 py-1 bg-gray-50">
                  <span className="text-gray-700">{row.display_name}</span>
                  {row.system_type && <span>{SYSTEM_TYPE_LABEL[row.system_type] ?? row.system_type}</span>}
                  <span className={CONNECTION_TONE_CLS[evidence.tone]}>
                    {evidence.label}
                  </span>
                  {evidence.rawStatus && (
                    <span className="text-gray-400 font-mono" dir="ltr">{evidence.rawStatus}</span>
                  )}
                  {row.last_tested_at && <span className="text-gray-400">נבדק: {fmtDate(row.last_tested_at)}</span>}
                  {row.agent_id && (
                    <Link to={`/agents/${row.agent_id}`} className="text-brand-600 hover:underline mr-auto">
                      {row.agent_name ?? "לעובד ←"}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </details>
      )}
    </div>
  );
}

const DOMAIN_DETAILS = {
  platform_scan:     ScanDetails,
  security_findings: FindingsDetails,
  backup:            BackupDetails,
  connections:       ConnectionsDetails,
};

const DOMAIN_DRILLDOWN = {
  platform_scan:     { to: "/platform-health", label: "בריאות פלטפורמה ←" },
  security_findings: { to: "/platform-health", label: "טיפול בממצאים — בריאות פלטפורמה ←" },
  backup:            { to: "/backup-guardian", label: "שומר הגיבויים ←" },
  connections:       { to: "/connections", label: "דף החיבורים ←" },
};

function DomainCard({ domainKey, domain }) {
  const Details = DOMAIN_DETAILS[domainKey];
  const drilldown = DOMAIN_DRILLDOWN[domainKey];
  const conditions = Array.isArray(domain.conditions) ? domain.conditions : [];
  const hasEvidence = domain.observed_at != null;
  return (
    <SectionCard
      title={DOMAIN_TITLE[domainKey]}
      icon={DOMAIN_ICON[domainKey]}
      testId={`guardian-domain-${domainKey}`}
      badge={<StateChip state={domain.state} label={domain.state_label} testId={`guardian-domain-state-${domainKey}`} />}
    >
      <div className="text-xs text-gray-500 space-y-0.5">
        <div>מקור ראיות: {domain.evidence_source}</div>
        {hasEvidence ? (
          <div>
            נצפה: {fmtDate(domain.observed_at)}
            {domain.freshness_hours != null && ` (לפני ${fmtAge(domain.freshness_hours)})`}
          </div>
        ) : (
          <div>טרם נצפו ראיות לתחום זה</div>
        )}
        {domain.stale_after_hours != null && <div>סף טריות: {domain.stale_after_hours} שעות</div>}
      </div>

      {Details && <Details details={domain.details} />}

      {conditions.length > 0 && (
        <div className="space-y-2" data-testid={`guardian-domain-conditions-${domainKey}`}>
          <h3 className="text-sm font-semibold text-gray-600">מצבים מזוהים ({conditions.length})</h3>
          {conditions.map((condition, i) => (
            <div key={i} className="border rounded-lg p-2 space-y-1 bg-white">
              <div className="flex items-center gap-2 flex-wrap">
                <SeverityBadge severity={condition.severity} />
                <span className="text-sm font-medium text-gray-800">{condition.title}</span>
                {condition.observed_at && (
                  <span className="text-xs text-gray-400 mr-auto">נצפה: {fmtDate(condition.observed_at)}</span>
                )}
              </div>
              <p className="text-xs text-gray-600">{condition.summary}</p>
              {condition.related && <RelatedBlock related={condition.related} />}
            </div>
          ))}
        </div>
      )}

      {domain.related && (
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-gray-600">קשרים</h3>
          <RelatedBlock related={domain.related} />
        </div>
      )}

      <div className="flex gap-3 text-xs flex-wrap">
        {domainKey === "platform_scan" && domain.details?.last_run_id && (
          <Link to={`/agent-runs/${domain.details.last_run_id}`} className="text-brand-600 hover:underline">
            לריצה האחרונה ←
          </Link>
        )}
        {drilldown && (
          <Link to={drilldown.to} className="text-brand-600 hover:underline">
            {drilldown.label}
          </Link>
        )}
      </div>
    </SectionCard>
  );
}

// ─── Findings (grouped ledger) ───────────────────────────────────────────────

function FindingRow({ finding }) {
  const freshness = finding.freshness || {};
  return (
    <div className="border rounded-lg p-3 space-y-1 bg-white">
      <div className="flex items-center gap-2 flex-wrap">
        <SeverityBadge severity={finding.severity} />
        <span className="text-sm font-medium text-gray-800">{finding.title}</span>
        {finding.escalation_state && (
          <span className="text-xs px-2 py-0.5 rounded-full border bg-gray-50 text-gray-600 border-gray-200 mr-auto">
            הסלמה: {ESCALATION_STATE_LABEL[finding.escalation_state] ?? finding.escalation_state}
          </span>
        )}
      </div>

      {finding.summary && <p className="text-xs text-gray-600">{finding.summary}</p>}

      <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap pt-0.5">
        <span>זוהה לראשונה: {fmtDate(finding.first_seen_at)}</span>
        <span aria-hidden="true">•</span>
        <span>
          נצפה לאחרונה: {freshness.age_hours != null ? `לפני ${fmtAge(freshness.age_hours)}` : "לא ידוע"}
        </span>
        {finding.occurrence_count != null && (
          <>
            <span aria-hidden="true">•</span>
            <span>מופעים: {finding.occurrence_count}</span>
          </>
        )}
      </div>

      {freshness.not_reobserved && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          לא נצפה מחדש מעל {freshness.reobserved_within_hours} שעות — ייתכן שהסריקה אינה מכסה אותו עוד
        </p>
      )}

      <div className="text-[11px] text-gray-400 flex items-center gap-2 flex-wrap">
        <span>מקור: {finding.provenance?.source_agent_name || "Guardian"}</span>
        {finding.provenance?.source_run_id && (
          <Link to={`/agent-runs/${finding.provenance.source_run_id}`} className="text-brand-500 hover:underline">
            לריצת המקור ←
          </Link>
        )}
      </div>

      {finding.related && <RelatedBlock related={finding.related} />}

      <EvidenceDisclosure entries={finding.evidence} />
    </div>
  );
}

function FindingsSection({ ledger, scanNeverRan }) {
  const [severityFilter, setSeverityFilter] = useState("all");
  const items = useMemo(() => (Array.isArray(ledger?.items) ? ledger.items : []), [ledger]);
  const counts = ledger?.counts || {};
  const escalation = ledger?.escalation || {};

  const groups = useMemo(() => {
    const filtered = items.filter((f) => severityFilter === "all" || f.severity === severityFilter);
    const byCategory = new Map();
    for (const f of filtered) {
      const key = f.category || "ללא קטגוריה";
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key).push(f);
    }
    const list = [...byCategory.entries()].map(([category, groupItems]) => {
      const sorted = [...groupItems].sort(
        (a, b) =>
          severityRank(a.severity) - severityRank(b.severity) ||
          new Date(b.last_seen_at || 0) - new Date(a.last_seen_at || 0)
      );
      return {
        category,
        label: CATEGORY_LABEL[category] || category,
        items: sorted,
        worst: sorted[0]?.severity,
      };
    });
    list.sort(
      (a, b) =>
        severityRank(a.worst) - severityRank(b.worst) ||
        a.label.localeCompare(b.label, "he")
    );
    return list;
  }, [items, severityFilter]);

  const ladderText = [
    ["awaiting_triage", "ממתין לטיפול"],
    ["in_treatment", "בטיפול"],
    ["watched", "במעקב"],
    ["handled", "טופל"],
    ["hidden", "הוסתר"],
  ]
    .map(([key, label]) => `${label}: ${escalation[key] ?? 0}`)
    .join(" · ");

  return (
    <SectionCard
      title="ממצאים לפי תחום"
      icon="🧭"
      testId="guardian-center-findings"
      badge={<span className="text-xs text-gray-400">מהרשומה המתמשכת של ממצאי האבטחה</span>}
    >
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="bg-gray-50 border border-gray-200 text-gray-700 rounded px-2 py-1">
          ממתינים לטיפול: <strong>{counts.open_total ?? 0}</strong>
        </span>
        <span className={`border rounded px-2 py-1 ${(counts.open_critical ?? 0) > 0 ? "bg-red-50 border-red-300 text-red-800" : "bg-gray-50 border-gray-200 text-gray-600"}`}>
          קריטיים פתוחים: <strong>{counts.open_critical ?? 0}</strong>
        </span>
        <span className="bg-gray-50 border border-gray-200 text-gray-700 rounded px-2 py-1">
          גבוהים פתוחים: <strong>{counts.open_high ?? 0}</strong>
        </span>
        <span className={`border rounded px-2 py-1 ${(counts.not_reobserved ?? 0) > 0 ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-gray-50 border-gray-200 text-gray-600"}`}>
          לא נצפו מחדש: <strong>{counts.not_reobserved ?? 0}</strong>
        </span>
        <span className="bg-gray-50 border border-gray-200 text-gray-600 rounded px-2 py-1">
          סה"כ ברשומה: <strong>{counts.total ?? 0}</strong>
        </span>
      </div>

      <div className="text-xs text-gray-500">סולם הסלמה — {ladderText}</div>

      <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs w-fit" role="group" aria-label="סינון לפי חומרה">
        {SEVERITY_FILTERS.map((filter, index) => (
          <button
            key={filter.key}
            type="button"
            onClick={() => setSeverityFilter(filter.key)}
            className={`px-2 py-1.5 ${index > 0 ? "border-r border-gray-200" : ""} ${
              severityFilter === filter.key ? "bg-gray-800 text-white font-medium" : "bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {items.length === 0 && scanNeverRan ? (
        <p className="text-sm text-gray-500">טרם בוצעה סריקת אבטחה — אין ממצאים להצגה.</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-gray-400">
          {severityFilter !== "all" ? "אין ממצאים בסינון הנוכחי" : "אין ממצאים ממתינים לטיפול"}
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group.category} className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-gray-600">{group.label}</h3>
                <span className="text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5">
                  {group.items.length === 1 ? "ממצא אחד" : `${group.items.length} ממצאים`}
                </span>
                <SeverityBadge severity={group.worst} />
              </div>
              <div className="space-y-2">
                {group.items.map((finding) => (
                  <FindingRow key={finding.id} finding={finding} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {ledger?.items_note && (
        <div className="flex items-center gap-2 flex-wrap text-xs text-gray-400">
          <span>{ledger.items_note}</span>
          <Link to="/platform-health" className="text-brand-600 hover:underline">
            לרשומה המלאה ←
          </Link>
        </div>
      )}
    </SectionCard>
  );
}

// ─── Audit trail ─────────────────────────────────────────────────────────────

function AuditSection({ audit }) {
  if (!audit) return null;
  const events = Object.entries(audit.by_event_type || {});
  const conditions = Array.isArray(audit.conditions) ? audit.conditions : [];
  return (
    <SectionCard
      title="יומן אבטחה"
      icon="📜"
      testId="guardian-center-audit"
      badge={<StateChip state={audit.state} label={audit.state_label} />}
    >
      <div className="text-xs text-gray-500 space-y-0.5">
        <div>
          חלון: {audit.window_hours} שעות · אירועים בחלון: <strong>{audit.total_events ?? 0}</strong>
          {audit.latest_at && ` · אחרון: ${fmtDate(audit.latest_at)}`}
        </div>
        <div className="text-gray-400">
          ספירות בלבד — היעדר אירועים אינו אישור תקינות; אירועי הסלמה חריגים משפיעים על מצב האבטחה
        </div>
      </div>

      {events.length > 0 && (
        <div className="flex flex-wrap gap-1.5 text-xs" data-testid="guardian-audit-events">
          {events.map(([eventType, count]) => (
            <span key={eventType} className="bg-gray-50 border border-gray-200 rounded px-2 py-0.5 text-gray-600">
              <span dir="ltr">{eventType}</span>: <strong>{count}</strong>
            </span>
          ))}
        </div>
      )}

      {conditions.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-600">אירועים לתשומת לב ({conditions.length})</h3>
          {conditions.map((condition, i) => (
            <div key={i} className="border rounded-lg p-2 space-y-1 bg-white">
              <div className="flex items-center gap-2 flex-wrap">
                <SeverityBadge severity={condition.severity} />
                <span className="text-sm font-medium text-gray-800">{condition.title}</span>
              </div>
              <p className="text-xs text-gray-600">{condition.summary}</p>
              {condition.related && <RelatedBlock related={condition.related} />}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Escalations ─────────────────────────────────────────────────────────────

function EscalationRow({ ticket }) {
  return (
    <div className="border rounded-lg px-3 py-2 space-y-1 bg-white text-xs">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
          {ticket.status ? ticketStatusLabel(ticket.status) : "—"}
        </span>
        <span className="text-gray-500">עדיפות: {TICKET_PRIORITY_LABEL[ticket.priority] ?? ticket.priority ?? "—"}</span>
        <span className="text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5">
          מקור: {ESCALATION_ORIGIN_LABEL[ticket.origin] ?? ticket.origin}
        </span>
        <span className="text-gray-500">הסלמה: {ESCALATION_STATE_LABEL[ticket.escalation_state] ?? ticket.escalation_state ?? "—"}</span>
        {ticket.age_hours != null && <span className="text-gray-400">נפתחה לפני {fmtAge(ticket.age_hours)}</span>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-gray-800 font-medium">{ticket.title}</span>
        <span className="text-gray-400">#{ticket.ticket_number}</span>
        <Link to={`/tickets/${ticket.id}`} className="text-brand-600 hover:underline mr-auto">
          לקריאה ←
        </Link>
      </div>
      {ticket.related && <RelatedBlock related={ticket.related} />}
    </div>
  );
}

function EscalationsSection({ escalations }) {
  if (!escalations) return null;
  const items = Array.isArray(escalations.items) ? escalations.items : [];
  const counts = escalations.counts || {};
  const byOrigin = counts.by_origin || {};
  return (
    <SectionCard
      title="הסלמות — קריאות שפתח השומר"
      icon="⬆️"
      testId="guardian-center-escalations"
      badge={<span className="text-xs text-gray-400">נקראות לפי סימני ההסלמה של השומר — ללא פעולה כאן</span>}
    >
      <div className="text-xs text-gray-500">
        {counts.active ?? 0} פעילות · {counts.closed ?? 0} סגורות (מתוך {counts.fetched ?? 0} שנקראו)
        {" · "}מקור: ממצאים {byOrigin.finding ?? 0} · גיבוי {byOrigin.backup ?? 0}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-400">לא נמצאו הסלמות פעילות שפתח השומר</p>
      ) : (
        <div className="space-y-2">
          {items.map((ticket) => (
            <EscalationRow key={ticket.id} ticket={ticket} />
          ))}
        </div>
      )}
      {escalations.items_note && <div className="text-xs text-gray-400">{escalations.items_note}</div>}
    </SectionCard>
  );
}

// ─── Employee rollup ─────────────────────────────────────────────────────────

function EmployeesRollup({ employees }) {
  const list = Array.isArray(employees) ? employees : [];
  return (
    <SectionCard
      title="עובדים דיגיטליים במצב הנוכחי"
      icon="🧑‍💼"
      testId="guardian-center-employees"
      badge={<span className="text-xs text-gray-400">קישורים לכרטיס העובד הקיים — כאן תצוגת מצב בלבד</span>}
    >
      <p className="text-xs text-gray-500">
        עובדים המשויכים למצבים הנוכחיים, לפי חומרה — כל רשומה מפרטת את הסיבות לשיוך
      </p>
      {list.length === 0 ? (
        <p className="text-sm text-gray-400">לא זוהו עובדים דיגיטליים המשויכים למצב הנוכחי</p>
      ) : (
        <div className="space-y-2">
          {list.map((employee) => (
            <div key={employee.agent_id} className="border rounded-lg p-3 space-y-1.5 bg-white">
              <div className="flex items-center gap-2 flex-wrap">
                <EmployeeAvatar name={employee.name} status={employee.availability_status} isEnabled={employee.is_enabled} size="sm" />
                <Link to={`/agents/${employee.agent_id}`} className="text-sm font-medium text-brand-600 hover:underline">
                  {employee.name}
                </Link>
                {employee.role_title && <span className="text-xs text-gray-500">{employee.role_title}</span>}
                <span className="mr-auto"><SeverityBadge severity={employee.worst_severity} /></span>
              </div>
              <ul className="text-xs text-gray-600 space-y-0.5 list-disc pr-5">
                {(employee.reasons || []).map((reason, i) => (
                  <li key={i}>
                    {reason.detail}
                    {reason.at && ` (${fmtDate(reason.at)})`}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Sources footer + raw payload ────────────────────────────────────────────

function SourcesFooter({ sources }) {
  const list = Array.isArray(sources) ? sources : [];
  return (
    <section data-testid="guardian-center-sources" className="bg-white rounded-xl border shadow-sm p-5 space-y-2">
      <h2 className="font-semibold text-gray-700 text-sm">מקורות הנתונים של המרכז</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs text-gray-500">
        {list.map((entry) => (
          <div key={entry.key} className="border border-gray-100 rounded px-2 py-1.5 space-y-0.5">
            <div className="flex items-center gap-2">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${entry.ok ? "bg-emerald-500" : "bg-gray-400"}`} aria-hidden="true" />
              <span className="text-gray-700">{entry.label}</span>
              <span className="mr-auto">{entry.ok ? `נצפה: ${fmtDate(entry.observed_at)}` : "לא נצפה"}</span>
            </div>
            <div className="text-gray-400 truncate" title={entry.source}>{entry.source}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400">
        המרכז מציג נתונים בלבד (קריאה בלבד). הרצת סריקה, טיפול בממצא, פתיחת קריאה ושינוי הגדרות מבוצעים בדפים הייעודיים שלהם.
      </p>
    </section>
  );
}

// The raw payload is the contract as received; secret-looking keys are still masked here so
// no credential-shaped field name or value can render anywhere on the page.
function redactSecrets(value) {
  if (Array.isArray(value)) return value.map(redactSecrets);
  if (value && typeof value === "object") {
    const out = {};
    for (const [key, entry] of Object.entries(value)) {
      if (EVIDENCE_SECRET_KEY_RE.test(key)) {
        out["«שדה מוסתר»"] = "«מוסתר»";
      } else {
        out[key] = redactSecrets(entry);
      }
    }
    return out;
  }
  return value;
}

function RawDisclosure({ data }) {
  return (
    <details data-testid="guardian-center-raw" className="bg-white rounded-xl border shadow-sm p-4 text-xs text-gray-500">
      <summary className="cursor-pointer select-none hover:text-gray-700">
        נתוני גלם (JSON) — לתחקור בלבד
      </summary>
      <div className="mt-1 text-gray-400">שדות בעלי שם רגיש מוסתרים («שדה מוסתר»).</div>
      <pre className="mt-2 overflow-x-auto bg-gray-50 border border-gray-100 rounded-lg p-3 text-[11px] leading-relaxed" dir="ltr">
        {JSON.stringify(redactSecrets(data), null, 2)}
      </pre>
    </details>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function GuardianCenterPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const [refreshing, setRefreshing] = useState(false);

  const centerQuery = useQuery({
    queryKey: ["guardian-center"],
    queryFn: guardianCenterApi.get,
    enabled: isAdmin,
    refetchInterval: 60_000,
  });

  const refresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    centerQuery.refetch().finally(() => setRefreshing(false));
  };

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto" dir="rtl">
        <div className="bg-white rounded-xl border shadow-sm p-8 text-center space-y-2">
          <h1 className="text-xl font-bold text-gray-800">🛡️ מרכז השומרים</h1>
          <p role="alert" className="text-sm text-gray-600">
            אין הרשאה — מרכז השומרים זמין למנהלים ובעלים בלבד.
          </p>
        </div>
      </div>
    );
  }

  const data = centerQuery.data;

  return (
    <div className="space-y-5 max-w-6xl mx-auto" dir="rtl" data-testid="guardian-center">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">🛡️ מרכז השומרים</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            תצוגת-על לקריאה בלבד: מצב השומרים — סריקה, ממצאים, גיבויים וחיבורים — עם חומרה, טריות, מקור והסלמות
          </p>
          {data?.generated_at && (
            <p className="text-xs text-gray-400 mt-0.5">נבנה: {fmtDate(data.generated_at)}</p>
          )}
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          className="text-xs border rounded-lg px-3 py-1.5 hover:bg-gray-50 text-gray-700 disabled:opacity-50"
        >
          {refreshing ? "מרענן…" : "רענון נתונים"}
        </button>
      </div>

      <OverallBanner
        failed={centerQuery.isError}
        loading={centerQuery.isLoading}
        stale={centerQuery.isError && Boolean(data)}
        data={data}
        onRetry={refresh}
      />

      {data?.domains && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="guardian-center-domain-cards">
          {DOMAIN_ORDER.filter((key) => data.domains[key]).map((key) => (
            <DomainCard key={key} domainKey={key} domain={data.domains[key]} />
          ))}
        </div>
      )}

      {data?.security_findings && (
        <FindingsSection
          ledger={data.security_findings}
          scanNeverRan={data.domains?.security_findings?.details?.scan_never_ran}
        />
      )}

      {data?.audit && <AuditSection audit={data.audit} />}

      {data?.escalations && <EscalationsSection escalations={data.escalations} />}

      {data?.related_employees && <EmployeesRollup employees={data.related_employees} />}

      {data?.sources && <SourcesFooter sources={data.sources} />}

      {data && <RawDisclosure data={data} />}
    </div>
  );
}
